import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

export function RoleBlueprintPage() {
  return (
    <GuideArticle
      slug="role-blueprint"
      title="Role blueprint & capability dimensions"
      intro={
        <p>
          The role blueprint is the second stage of the{" "}
          <Link to="/help/selection-process" className="text-link hover:underline">
            selection process
          </Link>
          . It translates an unstructured brief into a structured definition of what this role
          requires, and is the bar every candidate is later scored against.
        </p>
      }
    >
      <Section id="what" title="What a blueprint defines">
        <p>
          A blueprint captures 5–7 <strong>capability dimensions</strong> — the competencies that
          materially differentiate success in this role — and assigns each one a{" "}
          <strong>required level</strong> from 1 to 10. The order of dimensions reflects their
          relative importance: the most load-bearing competency is first.
        </p>
        <p>
          The blueprint is versioned. Every published version stamps an <em>origin</em> — system
          (generated by the blueprint workflow) or user (manually edited). Downstream artefacts
          (assessment matrix, interview pack, scoring matrix) read from the current version and
          regenerate when it changes.
        </p>
      </Section>

      <Section id="required-level" title="Required level (1–10)">
        <p>
          The required level is calibrated to the seniority and scope of this specific role — not to
          "how good at this skill can a person get". The 1–10 anchors are labelled (1–2 minimal /
          not required, 3–4 foundational, 5–6 solid practitioner, 7–8 advanced / leads, 9–10
          expert).
        </p>
        <p>
          The required level is the <em>bar</em>. A candidate scoring 4/5 on a level-8 dimension
          strongly meets a level-8 bar — not "is a 4 out of 10". See{" "}
          <Link to="/help/scoring-scales" className="text-link hover:underline">
            scoring scales
          </Link>{" "}
          for the dual-scale model.
        </p>
      </Section>

      <Section id="why-five-to-seven" title="Why five to seven dimensions">
        <p>
          Five dimensions is the recommended target. Above six, inter-rater reliability degrades
          quickly — assessors start trading dimensions off against each other instead of scoring
          them independently. The seventh slot is reserved for genuinely orthogonal requirements
          that cannot be folded into the others.
        </p>
      </Section>

      <Section id="ordering" title="Ordering matters">
        <p>
          Dimensions are ordered by importance. The order is consumed by the assessment matrix
          (first dimension's band gets the most scrutiny), the interview pack (probes for the
          highest-ordered dimensions are surfaced first), and the threshold rule (critical-
          dimension minimums are applied in order).
        </p>
      </Section>

      <Section id="origin" title="System-origin vs user-origin">
        <p>
          The blueprint and its downstream artefacts carry an origin stamp. If a version is{" "}
          <em>user-origin</em> (someone edited it directly), the regenerate gateway will refuse to
          overwrite it without an explicit confirmation. This is what protects manual judgements
          from being wiped by a routine "regenerate" click.
        </p>
      </Section>
    </GuideArticle>
  );
}
